<template>
  <div class="slider" @touchstart="onTouchStart">
    <div class="slider__stage">
      <button
        v-if="showNavElements"
        class="slider__button"
        data-track-text="appScreens.slider.next"
        @click="onLeftClick"
      >
        <dotcom-icon
          name="chevron-right"
          art="regular"
          class="slider__caret-icon"
          rotation="180"
          :color="
            isNextDisabled ? '--c-secondary-neutral-2' : '--c-primary-neutral-1'
          "
          hover-color="--c-primary-color-2"
        />
      </button>
      <div class="slider__content">
        <phone-wrapper>
          <slot name="left-area"></slot>
        </phone-wrapper>
        <slot name="right-area"></slot>
      </div>
      <button
        v-if="showNavElements"
        class="slider__button"
        data-track-text="appScreens.slider.previous"
        @click="onRightClick"
      >
        <dotcom-icon
          name="chevron-right"
          art="regular"
          class="slider__caret-icon"
          :rotation="null"
          :color="
            isPreviousDisabled
              ? '--c-secondary-neutral-2'
              : '--c-primary-neutral-1'
          "
          hover-color="--c-primary-color-2"
        />
      </button>
    </div>
    <ul v-if="showNavElements" class="slider__dots">
      <li
        v-for="(slide, index) in slides"
        :key="index"
        data-track-text="appScreens.slider.dot"
        class="slider__dot"
        :class="{ 'slider__dot--active': index === currentIndex }"
        @click="onDotClick(index)"
      ></li>
    </ul>
  </div>
</template>

<script>
import { trackAction, tracker } from '@/helpers/tracker'
import { sliderActionTypes } from '@/modules/appScreens/actionTypes'

import PhoneWrapper from './PhoneWrapper'

const SWIPE_THRESHOLD = 80

export default {
  name: 'Slider',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    PhoneWrapper,
  },
  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    slides: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    showNavElements() {
      return this.slides.length !== 1
    },
  },
  methods: {
    onLeftClick() {
      this.$emit('onUserAction', {
        type: sliderActionTypes.PREVIOUS,
      })
    },
    onRightClick() {
      this.$emit('onUserAction', {
        type: sliderActionTypes.NEXT,
      })
    },
    onDotClick(slideIndex) {
      this.$emit('onUserAction', {
        type: sliderActionTypes.DOT,
        slideIndex: slideIndex,
      })
    },

    onTouchStart(touchEvent) {
      if (this.isInvalidTouchEvent(touchEvent)) {
        return
      }
      const posXStart = touchEvent.changedTouches[0].clientX

      document.addEventListener(
        'touchend',
        touchEvent => this.onTouchEnd(touchEvent, posXStart),
        { once: true },
      )
    },
    onTouchEnd(touchEvent, posXStart) {
      if (this.isInvalidTouchEvent(touchEvent)) {
        return
      }
      const posXEnd = touchEvent.changedTouches[0].clientX
      const distanceX = Math.abs(posXEnd - posXStart)

      if (distanceX > SWIPE_THRESHOLD && posXStart < posXEnd) {
        this.onLeftClick()
        this.setTrackingData('swipeLeft')
      } else if (distanceX > SWIPE_THRESHOLD && posXStart > posXEnd) {
        this.onRightClick()
        this.setTrackingData('swipeRight')
      }

      document.removeEventListener('touchend', touchEvent =>
        this.onTouchEnd(touchEvent, touchEvent.changedTouches[0].clientX),
      )
    },
    isInvalidTouchEvent(touchEvent) {
      return touchEvent.changedTouches.length !== 1 || !this.showNavElements
    },
    setTrackingData(direction) {
      tracker.set({ data: { id: 'appScreens' } })
      trackAction(`appScreens.slider.${direction}`)
    },
  },
}
</script>

<style lang="postcss">
.slider {
  --slider__dot-dimension: 16px;
  display: flex;
  flex-direction: column;

  .slider__content,
  .slider__stage {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .slider__content {
    flex-direction: column;
  }

  .slider__button {
    display: inline-block;
    outline: none;
    border: none;
    background-color: transparent;

    &:hover {
      cursor: pointer;
    }
  }

  .slider__dots {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 76px;

    li {
      list-style: none;
    }

    .slider__dot {
      padding: var(--spacing-2);
      &:hover {
        cursor: pointer;
      }
      &:after {
        content: '';
        display: block;
        width: var(--slider__dot-dimension);
        height: var(--slider__dot-dimension);
        background-color: var(--c-secondary-neutral-3);
        list-style: none;
        border-radius: var(--slider__dot-dimension);
        overflow: hidden;
        transition: background-color 0.8s;
        cursor: pointer;
      }
    }
    .slider__dot--active::after {
      background-color: var(--c-primary-neutral-1);
    }
  }

  .slider__caret-icon {
    font-size: 25px;
  }
  .isDisabled {
    pointer-events: none;
    cursor: default;
  }
}

@media (--sm) {
  .slider {
    --slider__dot-dimension: 10px;
    .slider__content {
      flex-direction: row;
      padding: 0 var(--spacing-6);
    }
  }
}

@media (--lg) {
  .slider {
    .slider__content {
      padding: 0 var(--spacing-24);
    }
  }
}
</style>
